import { Project, ProjectPhoto, ProjectSettingsModeEnum } from '@/app/api';
import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import ProjectCard from '@/app/components/ProjectCard/ProjectCard';
import ProjectFinishedWidget from '@/app/components/ProjectFinishedWidget/ProjectFinishedWidget';
import { Routes } from '@/app/enums';
import { ProjectFinishedMoreStoriesHeadline } from '@/app/pages/ProjectDetail/components/ProjectFinishedMoreStoriesHeadline';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type ProjectClosedWidgetProps = {
  project: Project;
  moreProjects: Project[];
  handleReadMoreClick: () => void;
  coverImage: ProjectPhoto;
};

export const ProjectClosedWidget = ({
  project,
  moreProjects,
  handleReadMoreClick,
  coverImage,
}: ProjectClosedWidgetProps) => {
  const { t } = useTranslation();
  const projectMode = project.settings.mode;

  const projectFinishedMoreStoriesButtonText = useMemo(() => {
    if (projectMode === ProjectSettingsModeEnum.DonioPlus) {
      return t('pages.collection.projectFinishedMoreStoriesButton');
    }

    return t('pages.collection.charityCollectionFinishedMoreStoriesButton');
  }, [projectMode, t]);

  const projectFinishedMoreStoriesLink = useMemo(() => {
    if (projectMode === ProjectSettingsModeEnum.DonioPlus) {
      return `/${Routes.COMMERCIAL_PROJECTS}`;
    }

    return `/${Routes.CHARITY_COLLECTIONS}`;
  }, [projectMode]);

  return (
    <>
      <ProjectFinishedWidget
        coverImage={coverImage}
        project={project}
        onReadMoreClick={handleReadMoreClick}
        className="mb-[32px] lg:mb-[80px]"
      />
      <ProjectFinishedMoreStoriesHeadline
        actualSuccess={!!project.state.actualSuccess}
        projectMode={project.settings.mode}
      />
      <div className="overflow-x-auto flex lg:flex-wrap mx-[-24px] ml-[-38px] mb-[32px] lg:mb-[16px] px-[24px]">
        {[...moreProjects].slice(0, 4).map((project) => (
          <ProjectCard
            key={project.id}
            title={project.content.currentName}
            image={project.content.mainPhoto ?? undefined}
            href={`/${project.slug}`}
            currentAmountInCents={project.statistics.currentAmountInCents}
            targetAmountInCents={project.statistics.targetAmountInCents ?? undefined}
            donationsCount={project.statistics.donationsCount}
            className="ml-[16px] mb-[16px]"
          />
        ))}
      </div>
      <div className="flex justify-center mb-[110px]">
        <Button
          style={ButtonStyle.PRIMARY}
          size={ButtonSize.LARGE}
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.LINK, href: projectFinishedMoreStoriesLink }}
        >
          {projectFinishedMoreStoriesButtonText}
        </Button>
      </div>
    </>
  );
};
